import { useInvestmentsTradeDetail } from '@egw/api'
import { Image, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useParams } from 'react-router'
import { TradeField, type TradeFieldProps } from '../../../components/trade-field'

export const InvestmentsTradeDetails: FC = () => {
  const {
    alias = '',
    transactionId = '',
    tradeId = '',
  } = useParams<{ alias: string; transactionId: string; tradeId: string }>()

  const { t } = useTranslation('features-transactions')

  const { data, isLoading, isError } = useInvestmentsTradeDetail(
    {
      tradeId,
      alias: parseInt(alias),
    },
    {
      enabled: tradeId !== '' && alias !== '',
    },
  )

  const trade = data?.__typename === 'InvestmentTradeDetail' ? data : undefined

  const tradeDetails = ((): TradeFieldProps[] =>
    trade
      ? [
          {
            label: t('trade_details.labels.name'),
            value: trade.instrument?.name,
          },
          {
            label: t('trade_details.labels.status'),
            value: t(`trade_details.status.${trade.status}`),
          },
          {
            label: t('trade_details.labels.amount'),
            value: buildBasicCurrency(trade.amountCash),
          },
          {
            label: t('trade_details.labels.quantity'),
            value: trade.quantity,
          },
        ]
      : [])()

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-xl" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={t('trades.trade_details')}
          subtitle={t('trades.trades')}
          backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES_DETAILS.backPath
            ?.replace(':alias', alias.toString())
            .replace(':transactionId', transactionId.toString())}
        />
      ) : null}

      {trade ? (
        <div className="egw-container pt-0">
          <div className="flex flex-col gap-lg typo-primary-base-light">
            <div className="text-text-meta typo-primary-3xl flex flex-row gap-md items-center">
              {trade.instrument?.image ? (
                <Image src={trade.instrument.image} alt="holding image" className="w-3xl h-3xl" />
              ) : null}
              {trade.instrument?.symbol}
            </div>
            <div className="flex flex-col">
              {tradeDetails.map((tradeDetail: TradeFieldProps) => (
                <TradeField key={crypto.randomUUID()} {...tradeDetail} />
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {isError ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-3xl h-3xl fill-red-500 mb-sm" />
          <p className="text-text-error typo-primary-2xl-light mb-xl">{t('common.generic_error')}</p>
          <p className="text-text-body typo-primary-xl">{t('activity.error')}</p>
        </div>
      ) : null}
    </>
  )
}
